<template>
  <!-- Install Prompt: Chromium/Android/Desktop (beforeinstallprompt) -->
  <Transition name="slide-down">
    <div
      v-if="showChromiumInstallBanner"
      class="pwa-banner pwa-banner--install"
    >
      <div class="pwa-banner__icon">
        <v-icon size="24" color="primary">mdi-cellphone-arrow-down</v-icon>
      </div>
      <div class="pwa-banner__content">
        <div class="pwa-banner__title">Install App</div>
        <div class="pwa-banner__text">Add to your home screen for the best experience</div>
      </div>
      <div class="pwa-banner__actions">
        <v-btn variant="text" size="small" @click="dismissInstall">
          Later
        </v-btn>
        <v-btn color="primary" size="small" @click="handleInstall">
          Install
        </v-btn>
      </div>
    </div>
  </Transition>

  <!-- Install Prompt: iOS / iPadOS Safari (manual Add to Home Screen — opens instructions sheet) -->
  <Transition name="slide-down">
    <div
      v-if="showIosInstallBanner"
      class="pwa-banner pwa-banner--install"
    >
      <div class="pwa-banner__icon">
        <v-icon size="24" color="primary">mdi-home-plus-outline</v-icon>
      </div>
      <div class="pwa-banner__content">
        <div class="pwa-banner__title">Add to Home Screen</div>
        <div class="pwa-banner__text">
          Install {{ appDisplayName }} for quick access like a native app
        </div>
      </div>
      <div class="pwa-banner__actions">
        <v-btn variant="text" size="small" @click="dismissInstall">
          Later
        </v-btn>
        <v-btn color="primary" size="small" @click="openIosInstallSheet">
          How to install
        </v-btn>
      </div>
    </div>
  </Transition>

  <PWAInstallIosSheet
    v-model="showIosInstallSheet"
    :app-name="appDisplayName"
    :logo-url="appLogoUrl"
  />

  <!-- Offline Indicator -->
  <Transition name="slide-down">
    <div
      v-if="!isOnline"
      class="pwa-offline-indicator"
    >
      <v-icon size="16" color="warning">mdi-wifi-off</v-icon>
      <span>You're offline</span>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { usePWA, hasUserOpenedInstalledPwa } from '@/composables/usePWA'
import { useSettingsStore } from '@/stores/settings'
import { isIOSLike, isSafari } from '@/utils/platformDetection'
import PWAInstallIosSheet from '@/components/pwa/PWAInstallIosSheet.vue'

const settingsStore = useSettingsStore()
const {
  isInstallable,
  isStandalone,
  isOnline,
  installApp,
} = usePWA()

const showInstallBanner = ref(false)
const showIosInstallSheet = ref(false)

const appDisplayName = computed(() => settingsStore.getSiteName())
const appLogoUrl = computed(() => settingsStore.getSiteLogo())

/** Android / Chrome / Edge / desktop: native install prompt. */
const showChromiumInstallBanner = computed(
  () =>
    showInstallBanner.value &&
    !isStandalone.value &&
    isInstallable.value &&
    !isIOSLike(),
)

/** iPhone / iPad / iPadOS Safari only — A2HS sheet is Safari-specific. */
const showIosInstallBanner = computed(
  () =>
    showInstallBanner.value &&
    !isStandalone.value &&
    isIOSLike() &&
    isSafari() &&
    !hasUserOpenedInstalledPwa(),
)

// Check if user dismissed install banner recently
const DISMISS_KEY = 'pwa_install_dismissed'
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000 // 7 days

/** True when banner may show (not dismissed within window). Fail open on storage errors. */
function checkDismissed() {
  try {
    const dismissed = localStorage.getItem(DISMISS_KEY)
    if (!dismissed) return true
    const dismissedAt = parseInt(dismissed, 10)
    if (!Number.isFinite(dismissedAt)) return true
    // Clamp future-dated clocks so a skewed dismiss ts can't suppress forever.
    const ageMs = Date.now() - Math.min(dismissedAt, Date.now())
    if (ageMs < DISMISS_DURATION) {
      return false
    }
  } catch {
    /* private mode / Block All Cookies */
  }
  return true
}

function tryShowInstallBanner() {
  if (checkDismissed()) {
    showInstallBanner.value = true
  }
}

function openIosInstallSheet() {
  showInstallBanner.value = false
  showIosInstallSheet.value = true
}

onMounted(() => {
  tryShowInstallBanner()
})

async function handleInstall() {
  const installed = await installApp()
  if (!installed) {
    // User dismissed, hide for a while
    dismissInstall()
  }
}

function dismissInstall() {
  showInstallBanner.value = false
  try {
    localStorage.setItem(DISMISS_KEY, Date.now().toString())
  } catch {
    /* private mode / quota */
  }
}

</script>

<style lang="scss" scoped>
.pwa-banner {
  position: fixed;
  top: calc(64px + env(safe-area-inset-top, 0px) + 12px); // Below header with safe area
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.24);
  z-index: var(--z-sidebar); // Just below header (3002) but above content
  max-width: 400px;
  width: calc(100% - 32px);

  &__icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(var(--v-theme-primary), 0.1);
    border-radius: 10px;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-weight: 600;
    font-size: 0.875rem;
    color: rgb(var(--v-theme-on-surface));
  }

  &__text {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.7);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }
}

.pwa-offline-indicator {
  position: fixed;
  top: 64px; // Below header
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(var(--v-theme-warning), 0.15);
  border: 1px solid rgba(var(--v-theme-warning), 0.3);
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  color: rgb(var(--v-theme-warning));
  z-index: var(--z-dropdown);
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-20px);
}

// Responsive - mobile uses smaller header (56px)
@media (max-width: 480px) {
  .pwa-banner {
    top: calc(56px + env(safe-area-inset-top, 0px) + 8px);
    flex-wrap: wrap;

    &__content {
      flex-basis: calc(100% - 52px);
    }

    &__actions {
      width: 100%;
      justify-content: flex-end;
      margin-top: 8px;
    }
  }
}
</style>
